"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.Text = void 0;
require("./Text.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _conditionalAttributes = require("../../util/conditionalAttributes/conditionalAttributes");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * Text component
 *
 * @method
 * @param { TextProps} props
 * @returns Text
 */
/**
 * Primary UI component for user interaction
 */

const Text = props => {
  const {
    ariaHidden,
    children,
    className,
    htmlTag = defaultProps.htmlTag,
    id,
    linkStyle = defaultProps.linkStyle,
    screenReader = defaultProps.screenReader,
    textAlign = defaultProps.textAlign,
    textBold = defaultProps.textBold,
    textColor = defaultProps.textColor,
    textDecoration = defaultProps.textDecoration,
    textItalic,
    textStyle = defaultProps.textStyle
  } = props;
  const textCopy = textBold === true ? /*#__PURE__*/_react.default.createElement("strong", null, children) : children;
  const TagName = htmlTag;
  const componentClassName = (0, _classnames.default)('pal-c-Text', {
    [`pal-c-Text__style--${textStyle}`]: textStyle,
    [`pal-c-Text__color--${textColor}`]: textColor,
    [`pal-c-Text__align--${textAlign}`]: textAlign,
    [`pal-c-Text__${linkStyle}`]: linkStyle,
    'pal-c-Text__isScreenReader': screenReader === true,
    'pal-c-Text__fontStyle--italic': textItalic === true,
    'pal-c-Text__fontStyle--bold': textBold === true,
    [`pal-c-Text__decoration--${textDecoration}`]: textDecoration,
    [`${className}`]: className
  });
  if (!children) {
    return null;
  }
  const conditionalProps = (0, _conditionalAttributes.conditionalAttributes)({
    'aria-hidden': ariaHidden ? true : undefined
  });
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, textCopy && /*#__PURE__*/_react.default.createElement(TagName, _extends({
    className: componentClassName,
    id: id
  }, conditionalProps), textCopy));
};

/**
 * Property type definitions
 * @typedef TextProps
 * @type {object}
 * @property {boolean} ariaHidden - sets the aria-hidden to the text container
 * @property {node} children - children slot for the text
 * @property {string} textStyle - Sets the text style
 * @property {string} htmlTag - Sets the HTML tag for the container
 * @property {string} id - Sets the id for the container
 * @property {string} textAlign - Sets the text alignment
 * @property {string} textColor - Sets the text color from Ulta palette
 * @property {string} linkStyle - Sets the value for the linkStyle oneOf('hiddenLink', 'textLink', 'textLinkSecondary', 'textButton' )
 * @property {boolean} textItalic - Sets the font-style (italic)
 * @property {string} textDecoration - Sets the text decoration (underline / strike through)
 * @property {boolean} textBold - Sets the textBold bold to current if present
 */
exports.Text = Text;
const propTypes = exports.propTypes = {
  /**
   * Sets the aria-hidden property to the text container
  */
  ariaHidden: _propTypes.default.bool,
  /**
   * Children slot where components can be nested
   */
  children: _propTypes.default.node,
  /**
   * Style of the text.
   *
   * Default font weight in code is 300. In figma, this visually matches the 450 font weight.
   * - **body-xlg:** styles the text body-lg
   * - **body-lg:** styles the text body-lg
   * - **body:** styles the text body
   * - **body-sm:** styles the text body-sm
   * - **label:** styles the text label
   * - **label-sm:** styles the text label-sm
   * - **title-xlg:** styles the text title-xlg
   * - **title-lg:** styles the text title-lg
   * - **title:** styles the text title
   * - **title-sm:** styles the text title-sm
   * - **title-xs:** styles the text title-xs
   * - **title-xxs:** styles the text title-xxs
   * - **headline:** styles the text headline
   * - **headline-lg:** styles the text headline-lg
   * - **headline-sm:** styles the text headline-sm
   * - **display-xlg:** styles the text display-xlg
   * - **display-sm:** styles the text display-sm
   * - **displayBody:** styles the text displayBody
   * - **displayBody-sm:** styles the text displayBody-sm
   * - **eyebrow:** styles the text eyebrow
   * - **button:** styles the text button
   * - **button-compact:** styles the text button-compact
   * Default is body
   */
  textStyle: _propTypes.default.oneOf(['body-xlg', 'body-lg', 'body', 'body-sm', 'label', 'label-sm', 'title-xlg', 'title-lg', 'title', 'title-sm', 'title-xs', 'title-xxs', 'headline-lg', 'headline', 'headline-sm', 'display-xlg', 'display-lg', 'display', 'display-sm', 'displayBody', 'displayBody-sm', 'eyebrow', 'button', 'button-compact']),
  /**
   * Sets the HTML tag for text
   * - **p:** sets the HTML tag to p
   * - **span:** sets the HTML tag to span
   * - **h1:** sets the HTML tag to h1
   * - **h2:** sets the HTML tag to h2
   * - **h3:** sets the HTML tag to h3
   * - **h4:** sets the HTML tag to h4
   * - **h5:** sets the HTML tag to h5
   * - **h6:** sets the HTML tag to h6
   * Default is p
   */
  htmlTag: _propTypes.default.oneOf(['p', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  /**
   * Sets the id of the text
   */
  id: _propTypes.default.string,
  /**
   * Sets the alignment for text
   * - **left:** aligns the text left
   * - **center:** aligns the text center
   * - **right:** aligns the text right
   * Default is left
   */
  textAlign: _propTypes.default.oneOf(['left', 'center', 'right']),
  /**
   * Optional color overrides
   * - **content-brand-01:** sets the DXL color to brand-01
   * - **content-brand-02:** sets the DXL color to brand-02
   * - **content-brand-02-strong:** sets the DXL color to brand-02-strong
   * - **content-brand-03:** sets the DXL color to brand-03
   * - **content-brand-04:** sets the DXL color to brand-04
   * - **content-brand-04-strong:** sets the DXL color to brand-04-strong
   * - **content-default:** sets the DXL color to default
   * - **content-default-disabled:** sets the DXL color to default-disabled
   * - **content-knockout:** sets the DXL color to knockout
   * - **content-subtle:** sets the DXL color to subtle
   * - **content-xsubtle:** sets the DXL color to xsubtle
   * - **content-error:** sets the DXL color to error
   * - **content-notification:** sets the DXL color to notification
   * - **content-validation:** sets the DXL color to validation
   * - **content-warning:** sets the DXL color to warning
   * - **brand-01:** sets the color to brand-01
   * - **brand-02:** sets the color to brand-02
   * - **brand-02-strong:** sets the color to brand-02-strong
   * - **brand-03:** sets the color to brand-03
   * - **brand-04:** sets the color to brand-04
   * - **brand-04-strong:** sets the color to brand-04-strong
   * - **default:** sets the color to default
   * - **default-disabled:** sets the color to default-disabled
   * - **knockout:** sets the color to knockout
   * - **subtle:** sets the color to subtle
   * - **xsubtle:** sets the color to xsubtle
   * - **error:** sets the color to error
   * - **notification:** sets the color to notification
   * - **validation:** sets the color to validation
   * - **warning:** sets the color to warning
   */
  textColor: _propTypes.default.oneOf([/* palette colors from DXL */
  'content-brand-01', 'content-brand-02', 'content-brand-02-strong', 'content-brand-03', 'content-brand-04', 'content-brand-04-strong', 'content-default', 'content-default-disabled', 'content-knockout', 'content-subtle', 'content-xsubtle', 'content-error', 'content-info', 'content-notification', 'content-success', 'content-validation', 'content-warning', /* default palette colors */
  'brand-01', 'brand-02', 'brand-02-strong', 'brand-03', 'brand-04', 'brand-04-strong', 'default', 'default-disabled', 'knockout', 'subtle', 'xsubtle', 'error', 'info', 'notification', 'success', 'validation', 'warning']),
  /**
   * Optional text link font style
   * - **hiddenLink:** sets the title text to be a hidden inline link
   * - **textLink:** sets the text to be link style for whole card clickable links
   * - **textLinkSecondary:** sets the text to be secondary link style for the whole clickable card links
   * - **textButton:** sets the text to be button style for whole card clickable links
   */
  linkStyle: _propTypes.default.oneOf(['hiddenLink', 'textLink', 'textLinkSecondary', 'textButton']),
  /** Optional screenReader font style */
  screenReader: _propTypes.default.bool,
  /** Optional italic font style */
  textItalic: _propTypes.default.bool,
  /**
   * Optional text decorations
   * - **underline:** sets the text-decoration to underline
   * - **line-through:** sets the text-decoration to line-through
   */
  textDecoration: _propTypes.default.oneOf(['underline', 'line-through']),
  /** Optional bold styles */
  textBold: _propTypes.default.bool
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} textStyle - textStyle: 'body' - Sets the default value for the text style
 * @property {string} htmlTag - htmlTag: 'p' - Sets the default value for HTML tag for the container
 * @property {string} textAlign - textAlign: 'left' - Sets the default value for the text alignment
 * @property {string} textColor - textColor: undefined - Sets the default value for the text color from Ulta palette
 * @property {string} linkStyle - linkStyle: undefined - Sets the default value for the linkStyle oneOf('hiddenLink', 'textLink')
 * @property {boolean} textItalic - textItalic: false - Sets the default value for the font-style (italic)
 * @property {boolean} screenReader - screenReader: false - Sets the default value for the font-style (screenReader)
 * @property {string} textDecoration - textDecoration: undefined - Sets the default value for the text-decoration (underline / strike through)
 * @property {boolean} textBold - textBold: false - Sets the textBold bold to current if present
 */
const defaultProps = exports.defaultProps = {
  textStyle: 'body',
  htmlTag: 'p',
  linkStyle: undefined,
  textAlign: 'left',
  textColor: undefined,
  textItalic: false,
  screenReader: false,
  textDecoration: undefined,
  textBold: false
};
Text.propTypes = propTypes;
var _default = exports.default = Text;